import React from 'react'
import {useTranslation} from 'react-i18next'
import {FormModelType, useForm} from 'reactjs-use-form'

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useTheme,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import * as Yup from 'yup'

import MobileStickyActions, {
  MOBILE_STICKY_HEIGHT,
} from '@progyny/components/MobileStickyActions'
import PageLoader from '@progyny/components/PageLoader'
import {usePronounsQuery} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {CreateAccountSubProps} from '@progyny/pages/CreateAccount/types'
import {useMeStore} from '@progyny/store/meStore'
import {UNSET} from '@progyny/utils/constants'

const Preferences: React.ComponentType<CreateAccountSubProps> = ({
  navigator,
  initialValue,
  persistMemberData,
  callback,
  goBack,
}) => {
  const {t} = useTranslation()
  const isInConservativeCountry = useMeStore((s) => s.isInConservativeCountry)
  const getErrorMessage = useErrorCodes()
  const {data, loading} = usePronounsQuery()
  const emailValidation = Yup.string().email().nullable()

  const theme = useTheme()
  const isMobile = useResponsive('down', 'md')

  const formModel: FormModelType = {
    email: {
      value: initialValue?.privateEmail || '',
      required: false,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
    pronouns: {
      value: initialValue.pronouns === UNSET ? '' : initialValue.pronouns || '',
      required: false,
    },
    preferredGivenName: {
      value: initialValue.preferredGivenName || '',
      required: false,
    },
    preferredSurname: {
      value: initialValue.preferredSurname || '',
      required: false,
    },
  }

  const {values, errors, isDirty, isDisabled, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  async function handleSubmit() {
    await persistMemberData({
      privateEmail: values.email,
      pronouns: values.pronouns === '' ? UNSET : values.pronouns,
      preferredGivenName: values.preferredGivenName,
      preferredSurname: values.preferredSurname,
    })
    await callback()
  }

  function handleSkip() {
    callback()
  }

  const Navigator = navigator

  return loading ? (
    <PageLoader position={'center'} />
  ) : (
    <Box sx={{pb: {xs: MOBILE_STICKY_HEIGHT, md: 0}}}>
      <Box mt={4}>
        <Typography
          variant={'h6'}
          align={'center'}
          fontWeight={600}
          gutterBottom
        >
          {t('create_account_preferences_your_preferred_email')}
        </Typography>
        <TextField
          size={'medium'}
          variant='outlined'
          margin='dense'
          label={t('profile_personal_info_private_email_placeholder')}
          name='email'
          value={values.email}
          onChange={handleOnChange}
          error={errors.email.hasError}
          helperText={t(errors.email.message)}
          fullWidth
        />
        <Typography
          variant={'caption'}
          align={'center'}
          color={theme.palette.neutral['30']}
        >
          {t('create_account_preferences_once_it_has_been_verified')}
        </Typography>
      </Box>

      {isInConservativeCountry() === false && (
        <Box
          mt={4}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
        >
          <Typography
            variant={'h6'}
            align={'center'}
            fontWeight={600}
            gutterBottom
          >
            {t('create_account_preferences_your_preferred_pronouns')}
          </Typography>
          <Typography
            variant={'body1'}
            align={'center'}
            gutterBottom
            marginTop={1}
          >
            {t('create_account_preferences_how_should_we_refer_to')}
          </Typography>
          <Box mt={1}>
            <FormControl variant={'outlined'} margin={'dense'} fullWidth>
              <InputLabel id={'pronouns'}>
                {t('create_account_preferences_your_pronoun')}
              </InputLabel>
              <Select
                size={'medium'}
                labelId={'pronouns'}
                name='pronouns'
                margin={'dense'}
                label={t('create_account_preferences_your_pronoun')}
                placeholder={t('budget_reimbursement_documents_select_type')}
                onChange={handleOnChange}
                value={values.pronouns}
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                {data?.pronouns
                  .filter((item) => item.value !== UNSET)
                  .map((pronoun) => (
                    <MenuItem value={pronoun.value} key={pronoun.value}>
                      {pronoun.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      <Box
        mt={4}
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
      >
        <Typography
          variant={'h6'}
          align={'center'}
          fontWeight={600}
          gutterBottom
        >
          {t('create_account_preferences_your_preferred_name')}
        </Typography>
        <Typography
          variant={'body1'}
          align={'center'}
          gutterBottom
          marginTop={1}
        >
          {t('create_account_preferences_in_case_that_your_registered')}
        </Typography>
        <Stack mt={1} direction={{xs: 'column', md: 'row'}} spacing={2}>
          <TextField
            size={'medium'}
            label={t('profile_personal_info_preferred_first_name')}
            variant={'outlined'}
            fullWidth
            name={'preferredGivenName'}
            margin={'dense'}
            value={values.preferredGivenName}
            onChange={handleOnChange}
            inputProps={{
              'aria-label': t('create_account_preferred_name_given_name'),
            }}
          />
          <TextField
            size={'medium'}
            label={t('profile_personal_info_preferred_last_name')}
            variant={'outlined'}
            fullWidth
            name={'preferredSurname'}
            margin={'dense'}
            value={values.preferredSurname}
            onChange={handleOnChange}
            inputProps={{
              'aria-label': t('create_account_preferred_name_surname'),
            }}
          />
        </Stack>
      </Box>

      <MobileStickyActions>
        <Navigator
          buttonDisable={isDisabled}
          canSkip={!isDirty}
          submitDataRole={'preferences'}
          skipDataRole={'preferences'}
          label={t('common_continue')}
          onNextButtonClick={handleSubmit}
          onSkipButtonClick={handleSkip}
          onPrevButtonClick={goBack}
          backButtonType='outlined'
          fullWidth={isMobile}
        />
      </MobileStickyActions>
    </Box>
  )
}

export default Preferences
